import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  ArrowRight,
  ChevronsUpDown,
  Loader2,
  MousePointerClick,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import {
  useIntegrationConfig,
  useItsmCredentials,
} from "@/hooks/use-itsm-config";
import { ConfigEditor } from "@/components/itsm-config/config-editor";
import { SyncStateSection } from "@/components/itsm-config/sync-state-section";
import { ExportRulesSection } from "@/components/itsm-config/export-rules-section";
import { PmExportRulesSection } from "@/components/itsm-config/pm-export-rules-section";
import { MondayFieldMappingsSection } from "@/components/itsm-config/monday-field-mappings-section";
import { JiraFieldMappingsSection } from "@/components/itsm-config/jira-field-mappings-section";
import {
  MondayCustomFieldsSection,
  MondayImportSection,
  MondayStatusMappingSection,
  MondayTwoWaySyncSection,
} from "@/components/itsm-config/monday-sync-sections";
import { isLinkedOnly } from "@/lib/itsm-config/general-mapping";
import { mondayBoards, mondayImportBoards } from "@/lib/itsm-config/monday";
import { PriorityMappingSection } from "@/components/itsm-config/priority-mapping-section";
import { StatusMappingSection } from "@/components/itsm-config/status-mapping-section";
import { InboundSection } from "@/components/itsm-config/inbound-section";
import { FieldRulesSection } from "@/components/itsm-config/field-rules-section";
import { TagsSection } from "@/components/itsm-config/tags-section";
import {
  SectionRail,
  type RailSection,
} from "@/components/itsm-config/section-rail";
import { AgentSection } from "@/components/itsm-config/agent-section";
import {
  JiraLinkedTicketsSection,
  JiraTransitionsSection,
  ServiceNowDefaultGroupSection,
  ServiceNowMainTicketingSystemSection,
  ServiceNowMandatoryFieldsSection,
  ServiceNowExternalCategoriesSection,
} from "@/components/itsm-config/provider-specific-sections";
import {
  PROVIDER_FAMILY,
  PROVIDER_LABELS,
  type ProviderConfig,
  type ProviderFamily,
} from "@/lib/types/itsm-config";

const searchSchema = z.object({
  tenantId: z.string().optional(),
  credentialId: z.string().optional(),
});

export const Route = createFileRoute("/itsm-config")({
  component: ItsmConfigPage,
  validateSearch: zodValidator(searchSchema),
});

/** Sum of mapped entries across general_mapping rules for a given field. */
function mappedCount(
  draft: ProviderConfig,
  field: "priority_mapping" | "status_mapping" | "custom_field_values",
): number {
  return (draft.general_mapping ?? []).reduce(
    // eslint-disable-next-line security/detect-object-injection -- field is a typed literal union, not user input
    (n, rule) => n + Object.keys(rule[field] ?? {}).length,
    0,
  );
}

function buildSections(
  draft: ProviderConfig,
  tags: Record<string, string> | null,
  family: ProviderFamily,
  provider: string,
): RailSection[] {
  // Project-management providers only create a task — nothing to translate,
  // no inbound, no field rules. Tags are omitted too: every well-known tag key
  // is read by an ITSM provider (ServiceNow's outbound_record_type,
  // conversation sync's record type), so none of them does anything here.
  // monday is the exception: it can keep the item in sync both ways.
  if (family === "pm") {
    const sections: RailSection[] = [
      {
        id: "export",
        label: "Where tickets go",
        count: draft.export_mapping?.length ?? 0,
      },
    ];
    if (provider === "monday") {
      const boards = Object.values(draft.board_sync ?? {});
      sections.push(
        {
          id: "two-way-sync",
          label: "Two-way sync",
          count: draft.two_way_sync ? mondayBoards(draft).length : 0,
        },
        {
          id: "status",
          label: "Status translation",
          count: mappedCount(draft, "status_mapping"),
        },
        {
          id: "custom-fields",
          label: "Custom fields",
          count: boards.reduce(
            (n, b) => n + Object.keys(b.custom_field_columns ?? {}).length,
            0,
          ),
        },
        {
          id: "field-mappings",
          label: "Field mappings",
          count: boards.reduce(
            (n, b) => n + (b.field_mappings?.length ?? 0),
            0,
          ),
        },
        {
          id: "import",
          label: "Import from boards",
          count: Object.keys(mondayImportBoards(draft)).length,
        },
      );
    }
    return sections;
  }
  const sections: RailSection[] = [
    {
      id: "export",
      label: "Where tickets go",
      count: draft.export_mapping?.length ?? 0,
    },
    {
      id: "priority",
      label: "Priority translation",
      count: mappedCount(draft, "priority_mapping"),
    },
    {
      id: "status",
      label: "Status translation",
      count: mappedCount(draft, "status_mapping"),
    },
    {
      id: "inbound",
      label: "Importing tickets",
      // Under linked-only the watch rules are ignored: ServiceNow runs the one
      // linked-only pass, and other providers skip importing altogether.
      count: isLinkedOnly(draft.general_mapping)
        ? family === "servicenow"
          ? 1
          : 0
        : (draft.general_mapping ?? []).filter((r) => r.inbound?.watch).length,
    },
    {
      id: "fields",
      label: "Field rules",
      count: mappedCount(draft, "custom_field_values"),
    },
    {
      id: "agent",
      label: "AI agent",
      count: draft.agent?.enabled ? Math.max(draft.agent.rules?.length ?? 0, 1) : 0,
    },
  ];
  if (family === "jira") {
    sections.push({
      id: "transitions",
      label: "Jira transitions",
      count: Object.keys(draft.transitions ?? {}).length,
    });
    sections.push({
      id: "linked-tickets",
      label: "Linked tickets",
      count: draft.sync_linked_tickets ? 1 : 0,
    });
    sections.push({
      id: "field-mappings",
      label: "Field mappings",
      count: draft.field_mappings?.length ?? 0,
    });
  }
  if (family === "servicenow") {
    sections.push({
      id: "main-ticketing-system",
      label: "Main ticketing system",
      count: draft.main_ticketing_system ? 1 : 0,
    });
    sections.push({
      id: "default-group",
      label: "Default assignment group",
      count: draft.default_assignment_group ? 1 : 0,
    });
    sections.push({
      id: "mandatory-fields",
      label: "Mandatory field defaults",
      count: draft.mandatory_field_defaults?.length ?? 0,
    });
    sections.push({
      id: "categories",
      label: "External categories",
      count: draft.external_categories?.length ?? 0,
    });
  }
  sections.push({
    id: "tags",
    label: "Tags",
    count: Object.keys(tags ?? {}).length,
  });
  return sections;
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="border-muted-foreground/25 text-muted-foreground flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm">
      <MousePointerClick className="h-5 w-5 opacity-60" />
      {text}
    </div>
  );
}

function ItsmConfigPage() {
  const { tenantId, credentialId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantOpen, setTenantOpen] = useState(false);

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();
  const {
    data: credentials,
    isLoading: credsLoading,
    error: credsError,
  } =
    useItsmCredentials(tenantId);
  const { data: configData, isLoading: configLoading } = useIntegrationConfig(
    tenantId,
    credentialId,
  );

  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);
  const selectedCred = credentials?.find((c) => c.id === credentialId);

  const updateSearch = (
    updates: Partial<{ tenantId?: string; credentialId?: string }>,
  ) => {
    void navigate({ search: { tenantId, credentialId, ...updates } });
  };

  const family = selectedCred
    ? PROVIDER_FAMILY[selectedCred.provider]
    : undefined;
  const providerConfig: ProviderConfig | undefined =
    configData?.config.provider_config ??
    (selectedCred
      ? ({ provider: selectedCred.provider } as ProviderConfig)
      : undefined);

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-auto p-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">ITSM Config</h1>
        <p className="text-muted-foreground">
          Configure how Harmony syncs tickets with a customer&apos;s ITSM tool.
        </p>
      </div>

      {/* Context strip: which tenant + integration is being configured */}
      <div className="bg-muted/30 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border p-4">
        <div className="space-y-1.5">
          <p className="text-muted-foreground/70 text-[10px] font-semibold tracking-widest uppercase">
            Tenant
          </p>
          <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-[240px] justify-between bg-background"
              >
                <span className="truncate">
                  {selectedTenant ? tenantLabel(selectedTenant) : "Select a tenant..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0">
              <Command>
                <CellTenantsUnavailable failures={tenantFailures} />
                <CommandInput placeholder="Search tenants..." />
                <CommandList>
                  <CommandEmpty>
                    {tenantsLoading ? "Loading..." : "No tenant found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {sortedTenants.map((tenant) => (
                      <CommandItem
                        key={tenant.id}
                        value={tenantOptionValue(tenant)}
                        onSelect={() => {
                          updateSearch({
                            tenantId: tenant.id,
                            credentialId: undefined,
                          });
                          setTenantOpen(false);
                        }}
                        className={cn(tenantId === tenant.id && "bg-accent")}
                      >
                        {tenantLabel(tenant)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {tenantId && (
          <div className="space-y-1.5">
            <p className="text-muted-foreground/70 text-[10px] font-semibold tracking-widest uppercase">
              Integration
            </p>
            <div className="flex flex-wrap gap-2">
              {credsLoading ? (
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              ) : credentials && credentials.length > 0 ? (
                credentials.map((cred) => (
                  <Button
                    key={cred.id}
                    variant={cred.id === credentialId ? "default" : "outline"}
                    size="sm"
                    className={cred.id === credentialId ? "" : "bg-background"}
                    onClick={() => { updateSearch({ credentialId: cred.id }); }}
                  >
                    {PROVIDER_LABELS[cred.provider] ?? cred.provider}
                    <span className="ml-2 opacity-60">{cred.name}</span>
                  </Button>
                ))
              ) : credsError ? (
                <span className="text-destructive text-sm" role="alert">
                  Couldn't load this tenant's integrations: {credsError.message}
                </span>
              ) : (
                <span className="text-muted-foreground text-sm">
                  No ITSM integrations for this tenant.
                </span>
              )}
            </div>
          </div>
        )}

        {selectedCred && (
          <div className="ml-auto space-y-1.5 max-sm:hidden">
            <p className="text-muted-foreground/70 text-[10px] font-semibold tracking-widest uppercase">
              Direction
            </p>
            <div className="text-muted-foreground flex h-9 items-center gap-1.5 font-mono text-xs">
              <span className="text-foreground">Harmony</span>
              <ArrowRight className="h-3.5 w-3.5" />
              <span className="text-foreground">
                {PROVIDER_LABELS[selectedCred.provider] ?? selectedCred.provider}
              </span>
              <span className="ml-1">outbound</span>
            </div>
          </div>
        )}
      </div>

      {/* Config editor */}
      {!tenantId ? (
        <EmptyHint text="Select a tenant to begin." />
      ) : !credentialId ? (
        <EmptyHint text="Select an ITSM integration to configure." />
      ) : credsError ? null : configLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      ) : family && providerConfig && selectedCred ? (
        <>
          {/* The sync cursor belongs to the inbound collector. PM providers
              have no inbound sync, so there is nothing to show or rewind. */}
          {family !== "pm" && (
            <SyncStateSection tenantId={tenantId} credentialId={credentialId} />
          )}
          <ConfigEditor
            // Reset the draft when switching integrations.
            key={credentialId}
            tenantId={tenantId}
            credentialId={credentialId}
            provider={selectedCred.provider}
            config={configData?.config}
            serverProviderConfig={providerConfig}
          >
            {(draft, tags) => {
              const baseProps = {
                tenantId,
                credentialId,
                provider: selectedCred.provider,
                providerConfig: draft,
              };
              if (family === "pm") {
                return (
                  <div className="flex gap-8">
                    <SectionRail
                      sections={buildSections(
                        draft,
                        tags,
                        family,
                        selectedCred.provider,
                      )}
                    />
                    <div className="flex min-w-0 max-w-4xl flex-1 flex-col gap-6">
                      <section id="export" className="scroll-mt-6">
                        <PmExportRulesSection {...baseProps} />
                      </section>
                      {selectedCred.provider === "monday" && (
                        <>
                          <section id="two-way-sync" className="scroll-mt-6">
                            <MondayTwoWaySyncSection {...baseProps} />
                          </section>
                          <section id="status" className="scroll-mt-6">
                            <MondayStatusMappingSection {...baseProps} />
                          </section>
                          <section id="custom-fields" className="scroll-mt-6">
                            <MondayCustomFieldsSection {...baseProps} />
                          </section>
                          <section id="field-mappings" className="scroll-mt-6">
                            <MondayFieldMappingsSection {...baseProps} />
                          </section>
                          <section id="import" className="scroll-mt-6">
                            <MondayImportSection {...baseProps} />
                          </section>
                        </>
                      )}
                    </div>
                  </div>
                );
              }
              // Past the guard, `family` is narrowed to the ITSM families the
              // cards below are typed for.
              const cardProps = { ...baseProps, family };
              return (
                <div className="flex gap-8">
                  <SectionRail
                    sections={buildSections(
                      draft,
                      tags,
                      family,
                      selectedCred.provider,
                    )}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-6 max-w-4xl">
                    <section id="export" className="scroll-mt-6">
                      <ExportRulesSection {...cardProps} />
                    </section>
                    <section id="priority" className="scroll-mt-6">
                      <PriorityMappingSection {...cardProps} />
                    </section>
                    <section id="status" className="scroll-mt-6">
                      <StatusMappingSection {...cardProps} />
                    </section>
                    <section id="inbound" className="scroll-mt-6">
                      <InboundSection {...cardProps} />
                    </section>
                    <section id="fields" className="scroll-mt-6">
                      <FieldRulesSection {...cardProps} />
                    </section>
                    <section id="agent" className="scroll-mt-6">
                      <AgentSection {...cardProps} />
                    </section>
                    {family === "jira" && (
                      <section id="transitions" className="scroll-mt-6">
                        <JiraTransitionsSection {...baseProps} />
                      </section>
                    )}
                    {family === "jira" && (
                      <section id="linked-tickets" className="scroll-mt-6">
                        <JiraLinkedTicketsSection {...baseProps} />
                      </section>
                    )}
                    {family === "jira" && (
                      <section id="field-mappings" className="scroll-mt-6">
                        <JiraFieldMappingsSection {...baseProps} />
                      </section>
                    )}
                    {family === "servicenow" && (
                      <>
                        <section
                          id="main-ticketing-system"
                          className="scroll-mt-6"
                        >
                          <ServiceNowMainTicketingSystemSection {...baseProps} />
                        </section>
                        <section id="default-group" className="scroll-mt-6">
                          <ServiceNowDefaultGroupSection {...baseProps} />
                        </section>
                        <section id="mandatory-fields" className="scroll-mt-6">
                          <ServiceNowMandatoryFieldsSection {...baseProps} />
                        </section>
                        <section id="categories" className="scroll-mt-6">
                          <ServiceNowExternalCategoriesSection {...baseProps} />
                        </section>
                      </>
                    )}
                    <section id="tags" className="scroll-mt-6">
                      <TagsSection />
                    </section>
                  </div>
                </div>
              );
            }}
          </ConfigEditor>
        </>
      ) : (
        <EmptyHint text="This integration is not a supported ITSM provider." />
      )}
    </div>
  );
}
